import React, { useState } from 'react';
import { PAPER_TRADING_DISCLOSURE } from '../../utils/disclosure';
import {
  TrendingUp, TrendingDown, Copy, ChevronDown, ChevronRight,
  Calendar, RefreshCw, Clock
} from 'lucide-react';
import { DailySummary, AgentTrade } from '../../types';
import { pnlColor, pnlColor50, pnlColor30 } from '../../utils/pnl';
import { dteBadgeDays } from '../../utils/etDate';

interface TodaySummaryProps {
  summary: DailySummary | null;
  loading: boolean;
  agentTrades: AgentTrade[];
  onTakePosition: (trade: AgentTrade) => void;
}

const stratLabel = (s: string) => s === 'Cash-Secured Put' ? 'CSP' : s === 'Covered Call' ? 'CC' : s === 'SHARES' ? 'Shares' : s;

const stratBadgeColor = (s: string) =>
  s === 'CSP' ? 'bg-blue-500/10 text-blue-400' : s === 'Shares' ? 'bg-purple-500/10 text-purple-400' : 'bg-amber-500/10 text-amber-400';

const pnlClass = (val: number) => val >= 0 ? 'text-green-400' : 'text-red-400';

// B-028: the Today tab says it is paper trading, first, whatever state the day is in
const PaperTradingLine: React.FC = () => (
  <p className="text-11 text-amber-400/80 text-center">{PAPER_TRADING_DISCLOSURE}</p>
);

const TodaySummary: React.FC<TodaySummaryProps> = ({ summary, loading, agentTrades, onTakePosition }) => {
  if (loading) {
    return (
      <div className="space-y-4">
        <PaperTradingLine />
        <div className="flex flex-col items-center justify-center py-16">
          <RefreshCw className="w-5 h-5 animate-spin text-slate-500 mb-2" />
          <p className="text-xs text-slate-500">Loading today's summary...</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="space-y-4">
        <PaperTradingLine />
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <Calendar className="w-12 h-12 text-slate-600 mb-3" />
          <p className="text-sm text-slate-500">No trading data available</p>
          <p className="text-11 text-slate-600 mt-1">Run a cycle to generate activity</p>
        </div>
      </div>
    );
  }

  const mergedTakesArr = Object.values(summary.merged_takes || {});
  const mergedExitsArr = Object.values(summary.merged_exits || {});

  return (
    <div className="space-y-4">
      <PaperTradingLine />
      <div className="flex items-center gap-2 px-3 py-2 bg-slate-800/60 border border-slate-700/30 rounded-lg">
        <Clock className="w-3.5 h-3.5 text-indigo-400 shrink-0" />
        <div className="flex items-center justify-between w-full text-11">
          <span className="text-slate-300">
            Next refresh <span className="font-semibold text-indigo-300">{formatTimestamp(summary.next_refresh)}</span>
            <span className="text-slate-500 ml-1">({timeUntil(summary.next_refresh)})</span>
          </span>
          {!summary.is_current_day && (
            <span className="text-amber-400/80 text-10">
              Showing {formatDate(summary.today)}
            </span>
          )}
        </div>
      </div>

      <PerformanceBar agents={summary.agents} spyRet={summary.spy_ret} qqqRet={summary.qqq_ret} />

      <TakesSection
        mergedTakes={mergedTakesArr}
        totalTakes={(summary.takes || []).length}
        agentTrades={agentTrades}
        onTakePosition={onTakePosition}
        isCurrentDay={summary.is_current_day}
        entriesDate={summary.entries_date}
        effectiveDate={summary.today}
      />

      <MoversSection movers={summary.top_movers} losers={summary.top_losers} />

      <ExitsSection
        mergedExits={mergedExitsArr}
        totalExits={(summary.all_exits || []).length}
        allExits={summary.all_exits || []}
        exitsDate={summary.exits_date}
        effectiveDate={summary.today}
      />
    </div>
  );
};

// ============================================================
// Performance Bar
// ============================================================
export const PerformanceBar: React.FC<{
  agents: DailySummary['agents'];
  spyRet: number | null;
  qqqRet: number | null;
}> = ({ agents, spyRet, qqqRet }) => {
  const items = [
    { emoji: '📊', name: 'SPY', ret: spyRet, isBenchmark: true },
    { emoji: '📊', name: 'QQQ', ret: qqqRet, isBenchmark: true },
    ...agents.map(a => ({ emoji: a.emoji, name: a.name, ret: a.daily_return_pct, isBenchmark: false })),
  ];

  return (
    <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
      <span className="text-10 md:text-9 font-bold text-slate-500 uppercase shrink-0">Daily</span>
      {items.map((item) => (
        <span
          key={item.name}
          className={`shrink-0 text-10 flex items-center gap-0.5 px-1.5 py-1 rounded ${
            item.isBenchmark ? 'bg-slate-700/40 border border-slate-600/30' : 'bg-slate-800/40'
          }`}
        >
          <span>{item.emoji}</span>
          {item.ret === null || item.ret === undefined ? (
            // Benchmark data unavailable (outage) — render N/A, not a fake 0.0% (mirrors the newsletter).
            <span className="font-mono font-bold text-slate-500">N/A</span>
          ) : (
            <span className={`font-mono font-bold ${pnlClass(item.ret)}`}>
              {item.ret >= 0 ? '+' : ''}{item.ret.toFixed(1)}%
            </span>
          )}
        </span>
      ))}
    </div>
  );
};

// ============================================================
// Today's Takes — position card style (matches Positions tab)
// ============================================================
export const TakesSection: React.FC<{
  mergedTakes: any[];
  totalTakes: number;
  agentTrades: AgentTrade[];
  onTakePosition: (trade: AgentTrade) => void;
  isCurrentDay: boolean;
  entriesDate: string;
  effectiveDate: string;
}> = ({ mergedTakes, totalTakes, agentTrades, onTakePosition, isCurrentDay, entriesDate, effectiveDate }) => {
  const [collapsed, setCollapsed] = useState(true);
  const handleCopy = (take: any) => {
    const match = agentTrades.find(
      t => t.position.symbol === take.symbol && t.position.strategy === (take.strategy || '')
    );
    if (match) {
      onTakePosition(match);
    }
  };

  const avgConfidence = mergedTakes.length > 0
    ? Math.round(mergedTakes.reduce((s, t) => s + ((t.agents || []).reduce((a: number, ag: any) => a + (ag[2] || 0), 0) / Math.max((t.agents || []).length, 1)), 0) / mergedTakes.length)
    : 0;

  return (
    <div className="rounded-lg border border-cyan-500/15 bg-cyan-500/[0.02] overflow-hidden">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-cyan-500/10 border-b border-cyan-500/10 hover:bg-cyan-500/15 transition-colors"
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-cyan-400" /> : <ChevronDown className="w-3.5 h-3.5 text-cyan-400" />}
        <TrendingUp className="w-3.5 h-3.5 text-cyan-400" />
        <span className="text-xs font-bold text-cyan-300">New Entries</span>
        {totalTakes > 0 ? (
          <span className="text-10 px-1.5 py-0.5 bg-cyan-500/15 text-cyan-400 rounded font-medium">
            {totalTakes}
          </span>
        ) : (
          // No entries for the resolved trading day — clear even while collapsed.
          <span className="text-10 px-1.5 py-0.5 bg-slate-600/20 text-slate-500 rounded font-medium">
            None
          </span>
        )}
        <span className="text-10 text-slate-500">{formatDate(entriesDate)}</span>
        {mergedTakes.length > 0 && (
          <div className="ml-auto flex items-center gap-2 text-10">
            <span className="text-slate-400 font-medium">avg {avgConfidence}% conf</span>
          </div>
        )}
      </button>
      {!collapsed && (
        mergedTakes.length === 0 ? (
          <div className="px-3 py-6 text-center">
            <p className="text-xs text-slate-500">No New Entries</p>
            <p className="text-10 text-slate-600 mt-1">
              {isCurrentDay
                ? 'No positions opened today'
                : `No positions opened ${formatDate(entriesDate)}`}
            </p>
          </div>
        ) : (
          <div className="p-2 space-y-1.5">
            {mergedTakes.map((take: any, idx: number) => (
              <TakeCard key={`${take.symbol}-${idx}`} take={take} agentTrades={agentTrades} onCopy={() => handleCopy(take)} canCopy={isCurrentDay} />
            ))}
          </div>
        )
      )}
    </div>
  );
};

const TakeCard: React.FC<{ take: any; agentTrades: AgentTrade[]; onCopy: () => void; canCopy: boolean }> = ({ take, agentTrades, onCopy, canCopy }) => {
  const agents: Array<[string, string, number]> = take.agents || [];

  const matchedTrade = agentTrades.find(
    t => t.position.symbol === take.symbol && t.position.strategy === (take.strategy || '')
  );
  const pos = matchedTrade?.position;
  const ind = pos?.entry_indicators || {};
  const strat = stratLabel(take.strategy || '');

  const pnl = pos?.unrealized_pnl || 0;
  const pnlPct = pos?.unrealized_roi_pct ?? pos?.unrealized_pnl_pct ?? 0;
  const isPos = pnl >= 0;
  const isShares = strat === 'Shares';
  const hasStockLeg = pos?.has_stock_leg ?? false;

  const prem = pos ? pos.entry_price * pos.contracts * 100 : 0;
  const dte = dteBadgeDays(pos?.expiration || take.expiration);   // B-026: ET days
  const dteColor = dte < 0 ? 'text-red-400 bg-red-500/10' : dte <= 7 ? 'text-yellow-400 bg-yellow-500/10' : 'text-slate-400 bg-slate-700/30';

  const rsiVal = ind.rsi ?? 0;
  const rsiColor = rsiVal <= 30 ? 'text-green-400' : rsiVal >= 70 ? 'text-red-400' : 'text-slate-300';
  const macdColor = ind.macd_signal === 'bullish' ? 'text-green-400' : ind.macd_signal === 'bearish' ? 'text-red-400' : 'text-slate-300';
  const hasIndicators = rsiVal > 0 || ind.macd_signal || ind.support_level;
  const tfSignals = ind.timeframe_signals;

  const expiration = pos?.expiration || take.expiration || '';
  const strike = pos?.strike || take.strike || 0;
  const entryPrice = pos?.entry_price || take.entry_price || 0;
  const contracts = pos?.contracts || take.contracts || 1;

  return (
    <div className="group">
      <div className="px-3 py-1.5 bg-slate-800/60 border border-slate-700/30 rounded-lg">
        {/* Row 1: $SYMBOL + strategy badge + DTE */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-white font-mono">${take.symbol}</span>
            <span className={`text-10 md:text-9 px-1.5 py-0.5 rounded font-medium ${stratBadgeColor(strat)}`}>{strat}</span>
            <div className="flex items-center gap-0.5">
              {agents.map(([emoji, name]: [string, string, number], i: number) => (
                <span key={i} title={name} className="text-sm">{emoji}</span>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`text-10 font-mono px-1 py-0.5 rounded ${isShares ? 'text-slate-600 bg-slate-700/30' : dteColor}`}>
              {isShares ? <span className="text-slate-500">No expiry</span> : dte < 0 ? 'Expired' : <><span className="text-slate-500">Exp</span> {expiration?.slice(5)} <span className="text-slate-600">·</span> {dte}d left</>}
            </span>
            {canCopy && (
              <button
                onClick={onCopy}
                className="p-1 bg-cyan-500/15 hover:bg-cyan-500/25 text-cyan-400 rounded border border-cyan-500/20 transition-opacity"
                title="Copy this trade"
              >
                <Copy className="w-3 h-3" />
              </button>
            )}
          </div>
        </div>

        {/* Row 2: Contract details */}
        <div className="flex items-center gap-2 text-10 mb-1 pb-1 border-b border-slate-700/20 flex-wrap">
          {isShares ? (
            <>
              <span className="text-slate-500">Entry <span className="text-white font-mono">${(pos?.stock_entry_price || 0).toFixed(2)}</span></span>
              <span className="text-slate-600">·</span>
              <span className="text-slate-500">Current <span className="text-white font-mono">${(pos?.current_stock_price || pos?.stock_entry_price || 0).toFixed(2)}</span></span>
              <span className="text-slate-600">·</span>
              <span className="text-slate-500">{contracts * 100} shares</span>
            </>
          ) : (
            <>
              <span className="text-slate-500">Strike <span className="text-white font-mono">${strike.toFixed ? strike.toFixed(0) : strike}</span></span>
              <span className="text-slate-600">·</span>
              <span className="text-slate-500">Price <span className="text-white font-mono">${(ind.stock_price || pos?.current_stock_price || strike || 0).toFixed?.(1) ?? '0'}</span></span>
              <span className="text-slate-600">·</span>
              <span className="text-slate-500">Prem <span className="text-green-400 font-mono">{ind.premium_pct ? `${ind.premium_pct.toFixed(2)}%` : `$${entryPrice.toFixed?.(2) ?? '0.00'}`}</span></span>
              <span className="text-slate-600">·</span>
              <span className="text-slate-500">×{contracts}</span>
              <span className="text-green-400/60 font-mono">+${prem.toFixed(0)}</span>
              <span className="text-slate-600">·</span>
              <span className="text-slate-500">Opt</span>
              <span className="text-slate-400 font-mono">${entryPrice.toFixed?.(2) ?? '0.00'}</span>
              {pos?.current_price != null && pos.current_price > 0 && (
                <>
                  <span className="text-slate-600">→</span>
                  <span className={`font-mono ${((pos.option_pnl ?? 0) >= 0) ? 'text-green-400' : 'text-red-400'}`}>${pos.current_price.toFixed(2)}</span>
                </>
              )}
              {hasStockLeg && pos?.stock_entry_price && (
                <>
                  <span className="text-slate-600">·</span>
                  <span className="text-slate-500">Stk</span>
                  <span className="text-slate-400 font-mono">${pos.stock_entry_price.toFixed(1)}</span>
                  <span className="text-slate-600">→</span>
                  <span className={`font-mono ${pnlColor(pos.stock_pnl ?? 0)}`}>${(pos.current_stock_price || 0).toFixed(1)}</span>
                </>
              )}
            </>
          )}
          {pos && (
            <div className="ml-auto flex items-center gap-1.5">
              {!isShares && hasStockLeg ? (
                <>
                  <span className={`text-10 md:text-9 font-mono tabular-nums ${pnlColor50(pos.option_pnl ?? 0)}`}>
                    Opt:{(pos.option_pnl ?? 0) >= 0 ? '+' : ''}{(pos.option_pnl ?? 0).toFixed(0)}
                  </span>
                  <span className={`text-10 md:text-9 font-mono tabular-nums ${pnlColor50(pos.stock_pnl ?? 0)}`}>
                    Stk:{(pos.stock_pnl ?? 0) >= 0 ? '+' : ''}{(pos.stock_pnl ?? 0).toFixed(0)}
                  </span>
                  <span className="text-slate-700">|</span>
                </>
              ) : null}
              <span className={`text-11 font-bold font-mono tabular-nums ${isPos ? 'text-green-400' : 'text-red-400'}`}>
                {isPos ? '+' : ''}${Math.abs(pnl).toFixed(0)}
              </span>
              <span className={`text-10 md:text-9 font-mono tabular-nums ${isPos ? 'text-green-400/50' : 'text-red-400/50'}`}>
                {isPos ? '+' : ''}{pnlPct.toFixed(1)}% ROI
              </span>
              {pos.premium_pnl_pct != null && (
                <span className={`text-10 md:text-9 font-mono tabular-nums ${pnlColor30(pos.premium_pnl_pct)}`}>
                  {pos.premium_pnl_pct >= 0 ? '+' : ''}{pos.premium_pnl_pct.toFixed(0)}%p
                </span>
              )}
            </div>
          )}
        </div>

        {/* Row 3: Technicals + MTF signals */}
        {(hasIndicators || (ind.confluence_score != null && ind.confluence_score > 0) || tfSignals || ind.momentum_confirmed) && (
          <div className="flex items-center gap-1.5 text-10 md:text-9 flex-wrap">
            {rsiVal > 0 && (
              <span className="text-slate-500">RSI <span className={`font-mono ${rsiColor}`}>{rsiVal.toFixed(0)}{ind.rsi_signal === 'oversold' ? '↓' : ind.rsi_signal === 'overbought' ? '↑' : ''}</span></span>
            )}
            {ind.macd_signal && (
              <span className="text-slate-500">MACD <span className={`font-mono ${macdColor}`}>{ind.macd_signal}</span></span>
            )}
            {(ind.support_level != null && ind.support_level > 0) && (
              <span className="text-slate-500">Sup <span className="text-white font-mono">${ind.support_level.toFixed(0)}</span></span>
            )}
            {(ind.resistance_level != null && ind.resistance_level > 0) && (
              <span className="text-slate-500">Res <span className="text-white font-mono">${ind.resistance_level.toFixed(0)}</span></span>
            )}
            {ind.volume_signal && (
              <span className="text-slate-500">Vol <span className="text-slate-300 font-mono">{ind.volume_signal}{ind.volume_ratio ? ` ${ind.volume_ratio.toFixed(1)}x` : ''}</span></span>
            )}
            {tfSignals && (['weekly', 'daily', '4h', '1h'] as const).map(tf => {
              const sig = (tfSignals as Record<string, string>)[tf];
              if (!sig || sig === 'N/A') return null;
              const c = sig === 'bullish' ? 'bg-green-500/20 text-green-400' : sig === 'bearish' ? 'bg-red-500/20 text-red-400' : 'bg-slate-600/30 text-slate-400';
              return <span key={tf} className={`text-10 md:text-9 px-1 py-0.5 rounded font-mono ${c}`}>{tf.toUpperCase()}{sig === 'bullish' ? '↑' : sig === 'bearish' ? '↓' : '—'}</span>;
            })}
            {ind.momentum_confirmed && <span className="text-10 md:text-9 px-1 py-0.5 bg-blue-500/10 text-blue-400 rounded">Mom✓</span>}
          </div>
        )}

        {/* Row 4: Agents + confidence */}
        <div className="flex items-center gap-1 text-10 md:text-9 mt-0.5">
          <span className="text-slate-600">
            {agents.length} agent{agents.length !== 1 ? 's' : ''} — avg {Math.round(agents.reduce((s: number, a: any) => s + (a[2] || 0), 0) / agents.length)}% confidence
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Top Movers / Losers — distinct visual panels (always visible)
// ============================================================
const MoversSection: React.FC<{
  movers: DailySummary['top_movers'];
  losers: DailySummary['top_losers'];
}> = ({ movers, losers }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
    {/* Movers panel — green accent */}
    <div className="rounded-lg border border-green-500/20 bg-green-500/[0.03] overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-2 bg-green-500/10 border-b border-green-500/15">
        <TrendingUp className="w-3.5 h-3.5 text-green-400" />
        <span className="text-xs font-bold text-green-300">Top Movers</span>
        {movers.length > 0 && (
          <span className="text-10 px-1.5 py-0.5 bg-green-500/15 text-green-400 rounded font-medium ml-auto">
            {movers.length}
          </span>
        )}
      </div>
      <div className="p-2">
        {movers.length === 0 ? (
          <p className="text-xs text-slate-500">No data yet</p>
        ) : (
          <div className="space-y-1">
            {movers.map((p, i) => (
              <MoverCard key={`m-${i}`} position={p} accent="green" />
            ))}
          </div>
        )}
      </div>
    </div>
    {/* Losers panel — red accent */}
    <div className="rounded-lg border border-red-500/20 bg-red-500/[0.03] overflow-hidden">
      <div className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 border-b border-red-500/15">
        <TrendingDown className="w-3.5 h-3.5 text-red-400" />
        <span className="text-xs font-bold text-red-300">Top Losers</span>
        {losers.length > 0 && (
          <span className="text-10 px-1.5 py-0.5 bg-red-500/15 text-red-400 rounded font-medium ml-auto">
            {losers.length}
          </span>
        )}
      </div>
      <div className="p-2">
        {losers.length === 0 ? (
          <p className="text-xs text-slate-500">No data yet</p>
        ) : (
          <div className="space-y-1">
            {losers.map((p, i) => (
              <MoverCard key={`l-${i}`} position={p} accent="red" />
            ))}
          </div>
        )}
      </div>
    </div>
  </div>
);

const MoverCard: React.FC<{ position: any; accent: 'green' | 'red' }> = ({ position, accent }) => {
  const roi = position.roi_pct || 0;
  const dailyChg = position.daily_change_pct || 0;
  const isShares = position.is_shares;
  const hasStockLeg = position.has_stock_leg;
  const allAgents: Array<[string, string]> = position.all_agents || [];
  const borderColor = accent === 'green' ? 'border-green-500/10' : 'border-red-500/10';

  return (
    <div className={`bg-slate-800/50 border ${borderColor} rounded p-2`}>
      {/* Row 1: Symbol + Strategy | Daily Change + ROI */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <span className="text-xs font-bold text-white shrink-0">{position.symbol}</span>
          <span className="text-10 md:text-9 text-slate-500 shrink-0">{stratLabel(position.strategy)}</span>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className={`text-xs font-bold font-mono ${pnlClass(dailyChg)}`}>
            {dailyChg >= 0 ? '+' : ''}{dailyChg.toFixed(1)}%
          </span>
          <span className="text-slate-700">·</span>
          <span className={`text-10 md:text-9 font-mono ${pnlClass(roi)}`}>
            {roi >= 0 ? '+' : ''}{roi.toFixed(1)}%<span className="hidden md:inline"> ROI</span>
          </span>
        </div>
      </div>
      {/* Row 2: Agents */}
      <div className="flex items-center gap-1 mt-0.5 text-10 md:text-9 text-slate-500 flex-wrap">
        {allAgents.map(([emoji, name], i) => (
          <span key={i}>{emoji} {name}{i < allAgents.length - 1 ? ' ·' : ''}</span>
        ))}
      </div>
      {/* Row 3: Price details */}
      <div className="text-10 md:text-9 text-slate-500 mt-0.5 font-mono">
        {isShares ? (
          <span className={pnlClass(roi)}>
            ${(position.stock_entry_price || 0).toFixed(2)} → ${(position.current_stock_price || 0).toFixed(2)}
          </span>
        ) : hasStockLeg ? (
          <>
            <span className={pnlClass(position.current_price <= position.entry_price ? 1 : -1)}>
              Prem ${position.entry_price?.toFixed(2)} → ${position.current_price?.toFixed(2)}
            </span>
            <span className="text-slate-600"> · </span>
            <span className={pnlClass((position.current_stock_price || 0) - (position.stock_entry_price || 0))}>
              Stk ${(position.stock_entry_price || 0).toFixed(2)} → ${(position.current_stock_price || 0).toFixed(2)}
            </span>
          </>
        ) : (
          <>
            <span className={pnlClass(position.entry_price - position.current_price)}>
              ${position.entry_price?.toFixed(2)} → ${position.current_price?.toFixed(2)}
            </span>
            <span className="text-slate-600"> · </span>
            <span>Strike ${position.strike?.toFixed(0)}</span>
          </>
        )}
      </div>
    </div>
  );
};

// ============================================================
// Today's Closes — collapsible section (always visible)
// ============================================================
const ExitsSection: React.FC<{ mergedExits: any[]; totalExits: number; allExits: any[]; exitsDate: string; effectiveDate: string }> = ({ mergedExits, totalExits, allExits, exitsDate, effectiveDate }) => {
  const [collapsed, setCollapsed] = useState(true);
  const wins = allExits.filter(e => (e.pnl || 0) >= 0).length;
  const losses = allExits.length - wins;
  const totalPnl = allExits.reduce((s, e) => s + (e.pnl || 0), 0);

  return (
    <div className="rounded-lg border border-amber-500/15 bg-amber-500/[0.02] overflow-hidden">
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="w-full flex items-center gap-2 px-3 py-2 bg-amber-500/10 border-b border-amber-500/10 hover:bg-amber-500/15 transition-colors"
      >
        {collapsed ? <ChevronRight className="w-3.5 h-3.5 text-amber-400" /> : <ChevronDown className="w-3.5 h-3.5 text-amber-400" />}
        <span className="text-xs font-bold text-amber-300">Closed Positions</span>
        <span className="text-10 px-1.5 py-0.5 bg-amber-500/15 text-amber-400 rounded font-medium">
          {totalExits}
        </span>
        <span className="text-10 text-slate-500">{formatDate(exitsDate)}</span>
        <div className="ml-auto flex items-center gap-2 text-10">
          {wins > 0 && <span className="text-green-400 font-medium">{wins}W</span>}
          {losses > 0 && <span className="text-red-400 font-medium">{losses}L</span>}
          {totalExits > 0 && (
            <span className={`font-bold font-mono ${pnlClass(totalPnl)}`}>
              ${totalPnl >= 0 ? '+' : ''}{totalPnl.toLocaleString('en-US', { maximumFractionDigits: 0 })}
            </span>
          )}
        </div>
      </button>
      {!collapsed && (
        <div className="p-2">
          {mergedExits.length === 0 ? (
            <p className="text-xs text-slate-500">No closed positions</p>
          ) : (
            <div className="space-y-1">
              {mergedExits.map((exit: any, idx: number) => (
                <ExitCard key={`exit-${idx}`} exit={exit} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const ExitCard: React.FC<{ exit: any }> = ({ exit }) => {
  const [expanded, setExpanded] = useState(false);
  const roiPct = exit.roi_pct || 0;
  const premPnl = exit.premium_pnl_pct || 0;
  const pnlVal = exit.pnl || 0;
  const isWin = pnlVal >= 0;
  const agents: Array<[string, string, string]> = exit.agents || [];

  return (
    <div className="bg-slate-800/50 border border-amber-500/10 rounded p-2">
      {/* Row 1: icon + symbol + strategy | P&L */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <button onClick={() => setExpanded(!expanded)} className="flex items-center gap-1">
            {expanded ? <ChevronDown className="w-3 h-3 text-slate-500" /> : <ChevronRight className="w-3 h-3 text-slate-500" />}
          </button>
          <span className="text-sm">{isWin ? '✅' : '❌'}</span>
          <span className="text-xs font-bold text-white">{exit.symbol}</span>
          {exit.strategy && <span className="text-10 md:text-9 text-slate-500 shrink-0">{stratLabel(exit.strategy)}</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <span className={`text-xs font-bold font-mono ${pnlClass(roiPct)}`}>
            {roiPct >= 0 ? '+' : ''}{roiPct.toFixed(1)}%
          </span>
          <span className="text-10 md:text-9 text-slate-600">ROI</span>
          <span className="text-slate-700">·</span>
          <span className={`text-10 md:text-9 font-mono ${pnlClass(premPnl)}`}>
            {premPnl >= 0 ? '+' : ''}{premPnl.toFixed(0)}%
          </span>
          <span className="text-10 md:text-9 text-slate-600">prem</span>
          <span className="text-slate-700">·</span>
          <span className={`text-10 font-bold font-mono ${pnlClass(pnlVal)}`}>
            ${pnlVal >= 0 ? '+' : ''}{pnlVal.toLocaleString('en-US', { maximumFractionDigits: 0 })}
          </span>
        </div>
      </div>
      {/* Row 2: Agent list */}
      <div className="flex items-center gap-1 mt-1 text-10 md:text-9 text-slate-500 flex-wrap pl-5">
        {agents.map(([emoji, name, action]: [string, string, string], i: number) => (
          <span key={i}>
            {emoji} {name}{action === 'AUTO_CLOSE' ? ' (auto)' : ''}{i < agents.length - 1 ? ' ·' : ''}
          </span>
        ))}
      </div>
      {expanded && exit.reasoning && (
        <div className="mt-2 text-11 text-slate-400 bg-slate-900/40 rounded p-2 border-l-2 border-amber-500/30 ml-5">
          {exit.reasoning}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Helpers
// ============================================================
const formatDate = (dateStr: string) => {
  const d = new Date(dateStr + 'T12:00:00');
  return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
};

const formatTimestamp = (isoStr: string) => {
  try {
    // Force ET (CLAUDE.md rule). Append Z for naive-UTC strings so they aren't read as local.
    const norm = (isoStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(isoStr)) ? isoStr : isoStr + 'Z';
    const d = new Date(norm);
    if (isNaN(d.getTime())) return '';
    const ET = 'America/New_York';
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: ET }) + ', ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: ET }) + ' ET';
  } catch {
    return '';
  }
};

const timeUntil = (isoStr: string) => {
  try {
    const t = new Date(isoStr).getTime();
    if (isNaN(t)) return '';
    const diff = t - Date.now();
    if (diff <= 0) return 'soon';
    const hours = Math.floor(diff / 3600000);
    const mins = Math.floor((diff % 3600000) / 60000);
    if (hours >= 24) {
      const days = Math.floor(hours / 24);
      const remH = hours % 24;
      return `in ${days}d ${remH}h`;
    }
    return hours > 0 ? `in ${hours}h ${mins}m` : `in ${mins}m`;
  } catch {
    return '';
  }
};

export default TodaySummary;
